import loadable from '@loadable/component';
import { lazy } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { ProtectedRoute } from './components';
import config from './config';
import { AuthProvider } from './contexts/auth';
import { BulletinProvider } from './contexts/bulletin';
import { DocumentationLayout } from './layouts/Documentation';

// Main pages
const Action = loadable(() => import('./routes/Action'));
const Analytics = loadable(() => import('./routes/Analytics'));
const Changelog = loadable(() => import('./routes/Changelog'));
const Dashboard = loadable(() => import('./routes/Dashboard'));
const Event = loadable(() => import('./routes/Event'));
const Events = loadable(() => import('./routes/Events'));
const ForgotPassword = loadable(() => import('./routes/ForgotPassword'));
const Home = loadable(() => import('./routes/Home'));
const Identity = loadable(() => import('./routes/Identity'));
const IdentityEditor = loadable(() => import('./routes/IdentityEditor'));
const Identities = loadable(() => import('./routes/Identities'));
const IdentityGroup = loadable(() => import('./routes/IdentityGroup'));
const IdentityGroupEditor = loadable(
  () => import('./routes/IdentityGroupEditor')
);
const IdentityGroups = loadable(() => import('./routes/IdentityGroups'));
const Flow = loadable(() => import('./routes/Flow'));
const FlowEditor = loadable(() => import('./routes/FlowEditor'));
const Flows = loadable(() => import('./routes/Flows'));
const Webhook = loadable(() => import('./routes/Webhook'));
const WebhookEditor = loadable(() => import('./routes/WebhookEditor'));
const Webhooks = loadable(() => import('./routes/Webhooks'));
const IdentityGroupOAuthTest = loadable(
  () => import('./routes/IdentityGroupOAuthTest')
);
const Index = loadable(() => import('./routes/Index'));
const IndexEditor = loadable(() => import('./routes/IndexEditor'));
const Indexes = loadable(() => import('./routes/Indexes'));
const Item = loadable(() => import('./routes/Item'));
const ItemVersionCompare = loadable(
  () => import('./routes/ItemVersionCompare')
);
const ItemEditor = loadable(() => import('./routes/ItemEditor'));
const ItemVersions = loadable(() => import('./routes/ItemVersions'));
const Items = loadable(() => import('./routes/Items'));
const List = loadable(() => import('./routes/List'));
const ListEditor = loadable(() => import('./routes/ListEditor'));
const ListRulesEditor = loadable(() => import('./routes/ListRulesEditor'));
const Lists = loadable(() => import('./routes/Lists'));
const Login = loadable(() => import('./routes/Login'));
const NotFound = loadable(() => import('./routes/NotFound'));
const Pricing = loadable(() => import('./routes/Pricing'));
const Privacy = loadable(() => import('./routes/Privacy'));
const Profile = loadable(() => import('./routes/Profile'));
const ProfileEditor = loadable(() => import('./routes/ProfileEditor'));
const Register = loadable(() => import('./routes/Register'));
const ResetPassword = loadable(() => import('./routes/ResetPassword'));
const Security = loadable(() => import('./routes/Security'));
const Settings = loadable(() => import('./routes/Settings'));
const ManageSubscription = loadable(
  () => import('./routes/ManageSubscription')
);
const Subscriptions = loadable(() => import('./routes/Subscriptions'));
const Subscription = loadable(() => import('./routes/Subscription'));
const Terms = loadable(() => import('./routes/Terms'));
const Test = loadable(() => import('./routes/Test'));
const Token = loadable(() => import('./routes/Token'));
const TokenEditor = loadable(() => import('./routes/TokenEditor'));
const TokenAuthorize = loadable(() => import('./routes/TokenAuthorize'));
const Tokens = loadable(() => import('./routes/Tokens'));
const User = loadable(() => import('./routes/User'));
const UserEditor = loadable(() => import('./routes/UserEditor'));
const Users = loadable(() => import('./routes/Users'));
const UserDeleted = loadable(() => import('./routes/UserDeleted'));
const VerifyEmail = loadable(() => import('./routes/VerifyEmail'));

// Documentation
const DocumentationHome = lazy(() => import('./routes/documentation/Home'));
const DocumentationTokenPermissions = lazy(
  () => import('./routes/documentation/TokenPermissions')
);
const DocumentationEventLog = lazy(
  () => import('./routes/documentation/EventLog')
);
const DocumentationGettingStarted = lazy(
  () => import('./routes/documentation/GettingStarted')
);
const DocumentationTalkingToTheAPI = lazy(
  () => import('./routes/documentation/TalkingToTheAPI')
);
const DocumentationPathsAndPointers = lazy(
  () => import('./routes/documentation/PathsAndPointers')
);
const DocumentationRealtimeUpdates = lazy(
  () => import('./routes/documentation/RealtimeUpdates')
);
const DocumentationUsingTheSDKs = lazy(
  () => import('./routes/documentation/UsingTheSDKs')
);
const DocumentationVersionControl = lazy(
  () => import('./routes/documentation/VersionControl')
);
const DocumentationAPIReference = lazy(
  () => import('./routes/documentation/APIReference')
);
const DocumentationLists = lazy(() => import('./routes/documentation/Lists'));
const DocumentationListCreate = lazy(
  () => import('./routes/documentation/ListCreate')
);
const DocumentationListsIndex = lazy(
  () => import('./routes/documentation/ListsIndex')
);
const DocumentationListView = lazy(
  () => import('./routes/documentation/ListView')
);
const DocumentationListSearch = lazy(
  () => import('./routes/documentation/ListSearch')
);
const DocumentationListStats = lazy(
  () => import('./routes/documentation/ListStats')
);
const DocumentationListEvents = lazy(
  () => import('./routes/documentation/ListEvents')
);
const DocumentationListEvent = lazy(
  () => import('./routes/documentation/ListEvent')
);
const DocumentationListUpdate = lazy(
  () => import('./routes/documentation/ListUpdate')
);
const DocumentationListDelete = lazy(
  () => import('./routes/documentation/ListDelete')
);
const DocumentationItems = lazy(() => import('./routes/documentation/Items'));
const DocumentationItemCreate = lazy(
  () => import('./routes/documentation/ItemCreate')
);
const DocumentationItemsIndex = lazy(
  () => import('./routes/documentation/ItemsIndex')
);
const DocumentationItemsIndexData = lazy(
  () => import('./routes/documentation/ItemsIndexData')
);
const DocumentationItemsIndexPartialData = lazy(
  () => import('./routes/documentation/ItemsIndexPartialData')
);
const DocumentationItemView = lazy(
  () => import('./routes/documentation/ItemView')
);
const DocumentationItemViewData = lazy(
  () => import('./routes/documentation/ItemViewData')
);
const DocumentationItemViewPartialData = lazy(
  () => import('./routes/documentation/ItemViewPartialData')
);
const DocumentationItemStats = lazy(
  () => import('./routes/documentation/ItemStats')
);
const DocumentationItemEvents = lazy(
  () => import('./routes/documentation/ItemEvents')
);
const DocumentationItemEvent = lazy(
  () => import('./routes/documentation/ItemEvent')
);
const DocumentationItemRestore = lazy(
  () => import('./routes/documentation/ItemRestore')
);
const DocumentationItemUpdate = lazy(
  () => import('./routes/documentation/ItemUpdate')
);
const DocumentationItemUpdateData = lazy(
  () => import('./routes/documentation/ItemUpdateData')
);
const DocumentationItemReplaceData = lazy(
  () => import('./routes/documentation/ItemReplaceData')
);
const DocumentationItemPatchData = lazy(
  () => import('./routes/documentation/ItemPatchData')
);
const DocumentationItemUpdatePartialData = lazy(
  () => import('./routes/documentation/ItemUpdatePartialData')
);
const DocumentationItemReplacePartialData = lazy(
  () => import('./routes/documentation/ItemReplacePartialData')
);
const DocumentationItemPatchPartialData = lazy(
  () => import('./routes/documentation/ItemPatchPartialData')
);
const DocumentationItemDelete = lazy(
  () => import('./routes/documentation/ItemDelete')
);
const DocumentationItemDeletePartialData = lazy(
  () => import('./routes/documentation/ItemDeletePartialData')
);
const DocumentationIndexing = lazy(
  () => import('./routes/documentation/Indexing')
);
const DocumentationIndexes = lazy(
  () => import('./routes/documentation/Indexes')
);
const DocumentationIndexCreate = lazy(
  () => import('./routes/documentation/IndexCreate')
);
const DocumentationIndexesIndex = lazy(
  () => import('./routes/documentation/IndexesIndex')
);
const DocumentationIndexView = lazy(
  () => import('./routes/documentation/IndexView')
);
const DocumentationIndexStats = lazy(
  () => import('./routes/documentation/IndexStats')
);
const DocumentationIndexEvents = lazy(
  () => import('./routes/documentation/IndexEvents')
);
const DocumentationIndexEvent = lazy(
  () => import('./routes/documentation/IndexEvent')
);
const DocumentationIndexUpdate = lazy(
  () => import('./routes/documentation/IndexUpdate')
);
const DocumentationIndexRebuild = lazy(
  () => import('./routes/documentation/IndexRebuild')
);
const DocumentationIndexDelete = lazy(
  () => import('./routes/documentation/IndexDelete')
);
const DocumentationErrors = lazy(() => import('./routes/documentation/Errors'));
const DocumentationLimitsAndQuotas = lazy(
  () => import('./routes/documentation/LimitsAndQuotas')
);
const DocumentationGenerativeAPI = lazy(
  () => import('./routes/documentation/GenerativeAPI')
);
const DocumentationVariables = lazy(
  () => import('./routes/documentation/Variables')
);
const DocumentationTags = lazy(() => import('./routes/documentation/Tags'));
const DocumentationAuthentication = lazy(
  () => import('./routes/documentation/Authentication')
);
const DocumentationIdentities = lazy(
  () => import('./routes/documentation/Identities')
);
const DocumentationIdentitiesIndex = lazy(
  () => import('./routes/documentation/IdentitiesIndex')
);
const DocumentationIdentityCreate = lazy(
  () => import('./routes/documentation/IdentityCreate')
);
const DocumentationIdentityDelete = lazy(
  () => import('./routes/documentation/IdentityDelete')
);
const DocumentationIdentityEmailVerificationConfirm = lazy(
  () => import('./routes/documentation/IdentityEmailVerificationConfirm')
);
const DocumentationIdentityEmailVerificationRequest = lazy(
  () => import('./routes/documentation/IdentityEmailVerificationRequest')
);
const DocumentationIdentityGroups = lazy(
  () => import('./routes/documentation/IdentityGroups')
);
const DocumentationWebhooks = lazy(
  () => import('./routes/documentation/Webhooks')
);
const DocumentationFlows = lazy(() => import('./routes/documentation/Flows'));
const DocumentationFlowsEvents = lazy(
  () => import('./routes/documentation/FlowsEvents')
);
const DocumentationFlowsReference = lazy(
  () => import('./routes/documentation/FlowsReference')
);
const DocumentationIdentityOAuth = lazy(
  () => import('./routes/documentation/IdentityOAuth')
);
const DocumentationIdentityOAuthApple = lazy(
  () => import('./routes/documentation/IdentityOAuthApple')
);
const DocumentationIdentityOAuthDiscord = lazy(
  () => import('./routes/documentation/IdentityOAuthDiscord')
);
const DocumentationIdentityOAuthFacebook = lazy(
  () => import('./routes/documentation/IdentityOAuthFacebook')
);
const DocumentationIdentityOAuthMicrosoft = lazy(
  () => import('./routes/documentation/IdentityOAuthMicrosoft')
);
const DocumentationIdentityOAuthComplete = lazy(
  () => import('./routes/documentation/IdentityOAuthComplete')
);
const DocumentationIdentityOAuthGitHub = lazy(
  () => import('./routes/documentation/IdentityOAuthGitHub')
);
const DocumentationIdentityOAuthGoogle = lazy(
  () => import('./routes/documentation/IdentityOAuthGoogle')
);
const DocumentationIdentityOAuthProviders = lazy(
  () => import('./routes/documentation/IdentityOAuthProviders')
);
const DocumentationIdentityOAuthStart = lazy(
  () => import('./routes/documentation/IdentityOAuthStart')
);
const DocumentationIdentitySelfProviderLink = lazy(
  () => import('./routes/documentation/IdentitySelfProviderLink')
);
const DocumentationIdentitySelfProviderUnlink = lazy(
  () => import('./routes/documentation/IdentitySelfProviderUnlink')
);
const DocumentationIdentitySelfProviders = lazy(
  () => import('./routes/documentation/IdentitySelfProviders')
);
const DocumentationIdentityPasswordReset = lazy(
  () => import('./routes/documentation/IdentityPasswordReset')
);
const DocumentationIdentityPasswordResetConfirm = lazy(
  () => import('./routes/documentation/IdentityPasswordResetConfirm')
);
const DocumentationIdentityPasswordResetRequest = lazy(
  () => import('./routes/documentation/IdentityPasswordResetRequest')
);
const DocumentationIdentityDeleteSelf = lazy(
  () => import('./routes/documentation/IdentityDeleteSelf')
);
const DocumentationIdentityEvent = lazy(
  () => import('./routes/documentation/IdentityEvent')
);
const DocumentationIdentityEvents = lazy(
  () => import('./routes/documentation/IdentityEvents')
);
const DocumentationIdentityLogin = lazy(
  () => import('./routes/documentation/IdentityLogin')
);
const DocumentationIdentityLogout = lazy(
  () => import('./routes/documentation/IdentityLogout')
);
const DocumentationIdentityRegister = lazy(
  () => import('./routes/documentation/IdentityRegister')
);
const DocumentationIdentityStats = lazy(
  () => import('./routes/documentation/IdentityStats')
);
const DocumentationIdentityUpdate = lazy(
  () => import('./routes/documentation/IdentityUpdate')
);
const DocumentationIdentityUpdateSelf = lazy(
  () => import('./routes/documentation/IdentityUpdateSelf')
);
const DocumentationIdentityView = lazy(
  () => import('./routes/documentation/IdentityView')
);
const DocumentationIdentityViewSelf = lazy(
  () => import('./routes/documentation/IdentityViewSelf')
);
const DocumentationSchemaSync = lazy(
  () => import('./routes/documentation/SchemaSync')
);
const DocumentationWriteRules = lazy(
  () => import('./routes/documentation/WriteRules')
);
const DocumentationWriteRulesReference = lazy(
  () => import('./routes/documentation/WriteRulesReference')
);
const DocumentationWriteRulesRecipes = lazy(
  () => import('./routes/documentation/WriteRulesRecipes')
);
const DocumentationListRulesTest = lazy(
  () => import('./routes/documentation/ListRulesTest')
);
const DocumentationListRuleDenials = lazy(
  () => import('./routes/documentation/ListRuleDenials')
);
const DocumentationCommandLineTool = lazy(
  () => import('./routes/documentation/CommandLineTool')
);
const DocumentationMcp = lazy(() => import('./routes/documentation/Mcp'));
const DocumentationSyncSchema = lazy(
  () => import('./routes/documentation/SyncSchema')
);
const DocumentationSyncSchemaExport = lazy(
  () => import('./routes/documentation/SyncSchemaExport')
);
const DocumentationSyncSchemaMove = lazy(
  () => import('./routes/documentation/SyncSchemaMove')
);
const DocumentationTokenViewSelf = lazy(
  () => import('./routes/documentation/TokenViewSelf')
);
const DocumentationFlowRun = lazy(
  () => import('./routes/documentation/FlowRun')
);
const DocumentationMessagePack = lazy(
  () => import('./routes/documentation/MessagePack')
);
const DocumentationJSON5 = lazy(() => import('./routes/documentation/JSON5'));

const DocumentationListSchemas = lazy(
  () => import('./routes/documentation/ListSchemas')
);

const App = () => {
  return (
    <Router>
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <AuthProvider>
          <BulletinProvider>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/action" element={<Action />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/security" element={<Security />} />
              <Route
                path="/register"
                element={
                  <ProtectedRoute
                    requireLoggedIn={false}
                    redirectPath="/dashboard"
                  >
                    <Register />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <ProtectedRoute
                    requireLoggedIn={false}
                    redirectPath="/dashboard"
                  >
                    <Login />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verify-email"
                element={
                  <ProtectedRoute
                    requireLoggedIn={false}
                    redirectPath="/dashboard"
                  >
                    <VerifyEmail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <ProtectedRoute
                    requireLoggedIn={false}
                    redirectPath="/dashboard"
                  >
                    <ForgotPassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <ProtectedRoute redirectPath="/dashboard">
                    <ResetPassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test"
                element={
                  <ProtectedRoute
                    requireLoggedIn={true}
                    requireAdmin={true}
                    redirectPath="/login"
                  >
                    <Test />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Lists />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/create"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ListEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <List />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ListEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/rules"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ListRulesEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/items"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Items />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/items/create"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ItemEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/items/:itemId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Item />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/items/:itemId/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ItemEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/items/:itemId/versions"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ItemVersions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/items/:itemId/compare"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ItemVersionCompare />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/indexes"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Indexes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/indexes/create"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IndexEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/indexes/:indexId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lists/:listId/indexes/:indexId/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IndexEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tokens"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Tokens />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tokens/create"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <TokenEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tokens/authorize"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <TokenAuthorize />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tokens/:tokenId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Token />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tokens/:tokenId/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <TokenEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identities"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Identities />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identities/create"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IdentityEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identities/:identityId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Identity />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identities/:identityId/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IdentityEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identity-groups"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IdentityGroups />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identity-groups/create"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IdentityGroupEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identity-groups/oauth-test"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IdentityGroupOAuthTest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identity-groups/:groupId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IdentityGroup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/identity-groups/:groupId/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <IdentityGroupEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Flows />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows/create"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <FlowEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows/:flowId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Flow />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows/:flowId/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <FlowEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/webhooks"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Webhooks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/webhooks/create"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <WebhookEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/webhooks/:webhookId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Webhook />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/webhooks/:webhookId/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <WebhookEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Events />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/:eventId"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Event />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile/edit"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ProfileEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute
                    requireLoggedIn={true}
                    requireAdmin={true}
                    redirectPath="/login"
                  >
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute
                    requireLoggedIn={true}
                    requireAdmin={true}
                    redirectPath="/login"
                  >
                    <Subscriptions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions/:subscriptionId"
                element={
                  <ProtectedRoute
                    requireLoggedIn={true}
                    requireAdmin={true}
                    redirectPath="/login"
                  >
                    <Subscription />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage-subscription"
                element={
                  <ProtectedRoute requireLoggedIn={true} redirectPath="/login">
                    <ManageSubscription />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute
                    requireLoggedIn={true}
                    requireAdmin={true}
                    redirectPath="/login"
                  >
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute
                    requireLoggedIn={true}
                    requireAdmin={true}
                    redirectPath="/login"
                  >
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/:userId"
                element={
                  <ProtectedRoute
                    requireLoggedIn={true}
                    requireAdmin={true}
                    redirectPath="/login"
                  >
                    <User />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/:userId/edit"
                element={
                  <ProtectedRoute
                    requireLoggedIn={true}
                    requireAdmin={true}
                    redirectPath="/login"
                  >
                    <UserEditor />
                  </ProtectedRoute>
                }
              />
              <Route path="/user-deleted" element={<UserDeleted />} />
              <Route path="/docs" element={<DocumentationLayout />}>
                <Route index element={<DocumentationHome />} />
                <Route
                  path="token-permissions"
                  element={<DocumentationTokenPermissions />}
                />
                <Route path="event-log" element={<DocumentationEventLog />} />
                <Route
                  path="getting-started"
                  element={<DocumentationGettingStarted />}
                />
                <Route
                  path="talking-to-the-api"
                  element={<DocumentationTalkingToTheAPI />}
                />
                <Route
                  path="paths-and-pointers"
                  element={<DocumentationPathsAndPointers />}
                />
                <Route
                  path="realtime-updates"
                  element={<DocumentationRealtimeUpdates />}
                />
                <Route
                  path="using-the-sdks"
                  element={<DocumentationUsingTheSDKs />}
                />
                <Route
                  path="version-control"
                  element={<DocumentationVersionControl />}
                />
                <Route
                  path="list-schemas"
                  element={<DocumentationListSchemas />}
                />
                <Route
                  path="api-reference"
                  element={<DocumentationAPIReference />}
                />
                <Route path="lists" element={<DocumentationLists />} />
                <Route
                  path="list-create"
                  element={<DocumentationListCreate />}
                />
                <Route
                  path="lists-index"
                  element={<DocumentationListsIndex />}
                />
                <Route path="list-view" element={<DocumentationListView />} />
                <Route
                  path="list-search"
                  element={<DocumentationListSearch />}
                />
                <Route path="list-stats" element={<DocumentationListStats />} />
                <Route
                  path="list-events"
                  element={<DocumentationListEvents />}
                />
                <Route path="list-event" element={<DocumentationListEvent />} />
                <Route
                  path="list-update"
                  element={<DocumentationListUpdate />}
                />
                <Route
                  path="list-delete"
                  element={<DocumentationListDelete />}
                />
                <Route path="items" element={<DocumentationItems />} />
                <Route
                  path="item-create"
                  element={<DocumentationItemCreate />}
                />
                <Route
                  path="items-index"
                  element={<DocumentationItemsIndex />}
                />
                <Route
                  path="items-index-data"
                  element={<DocumentationItemsIndexData />}
                />
                <Route
                  path="items-index-partial-data"
                  element={<DocumentationItemsIndexPartialData />}
                />
                <Route path="item-view" element={<DocumentationItemView />} />
                <Route
                  path="item-view-data"
                  element={<DocumentationItemViewData />}
                />
                <Route
                  path="item-view-partial-data"
                  element={<DocumentationItemViewPartialData />}
                />
                <Route path="item-stats" element={<DocumentationItemStats />} />
                <Route
                  path="item-events"
                  element={<DocumentationItemEvents />}
                />
                <Route path="item-event" element={<DocumentationItemEvent />} />
                <Route
                  path="item-restore"
                  element={<DocumentationItemRestore />}
                />
                <Route
                  path="item-update"
                  element={<DocumentationItemUpdate />}
                />
                <Route
                  path="item-update-data"
                  element={<DocumentationItemUpdateData />}
                />
                <Route
                  path="item-replace-data"
                  element={<DocumentationItemReplaceData />}
                />
                <Route
                  path="item-patch-data"
                  element={<DocumentationItemPatchData />}
                />
                <Route
                  path="item-update-partial-data"
                  element={<DocumentationItemUpdatePartialData />}
                />
                <Route
                  path="item-replace-partial-data"
                  element={<DocumentationItemReplacePartialData />}
                />
                <Route
                  path="item-patch-partial-data"
                  element={<DocumentationItemPatchPartialData />}
                />
                <Route
                  path="item-delete"
                  element={<DocumentationItemDelete />}
                />
                <Route
                  path="item-delete-partial-data"
                  element={<DocumentationItemDeletePartialData />}
                />
                <Route path="indexing" element={<DocumentationIndexing />} />
                <Route path="indexes" element={<DocumentationIndexes />} />
                <Route
                  path="index-create"
                  element={<DocumentationIndexCreate />}
                />
                <Route
                  path="indexes-index"
                  element={<DocumentationIndexesIndex />}
                />
                <Route path="index-view" element={<DocumentationIndexView />} />
                <Route
                  path="index-stats"
                  element={<DocumentationIndexStats />}
                />
                <Route
                  path="index-events"
                  element={<DocumentationIndexEvents />}
                />
                <Route
                  path="index-event"
                  element={<DocumentationIndexEvent />}
                />
                <Route
                  path="index-update"
                  element={<DocumentationIndexUpdate />}
                />
                <Route
                  path="index-rebuild"
                  element={<DocumentationIndexRebuild />}
                />
                <Route
                  path="index-delete"
                  element={<DocumentationIndexDelete />}
                />
                <Route
                  path="limits-and-quotas"
                  element={<DocumentationLimitsAndQuotas />}
                />
                <Route path="errors" element={<DocumentationErrors />} />
                <Route
                  path="generative-api"
                  element={
                    config.generativeAPIEnabled ? (
                      <DocumentationGenerativeAPI />
                    ) : (
                      <Navigate to="/docs" replace />
                    )
                  }
                />
                <Route path="variables" element={<DocumentationVariables />} />
                <Route path="tags" element={<DocumentationTags />} />
                <Route
                  path="authentication"
                  element={<DocumentationAuthentication />}
                />
                <Route
                  path="identities"
                  element={<DocumentationIdentities />}
                />
                <Route
                  path="identities-index"
                  element={<DocumentationIdentitiesIndex />}
                />
                <Route
                  path="identity-create"
                  element={<DocumentationIdentityCreate />}
                />
                <Route
                  path="identity-delete"
                  element={<DocumentationIdentityDelete />}
                />
                <Route
                  path="identity-delete-self"
                  element={<DocumentationIdentityDeleteSelf />}
                />
                <Route
                  path="identity-event"
                  element={<DocumentationIdentityEvent />}
                />
                <Route
                  path="identity-events"
                  element={<DocumentationIdentityEvents />}
                />
                <Route
                  path="identity-login"
                  element={<DocumentationIdentityLogin />}
                />
                <Route
                  path="identity-logout"
                  element={<DocumentationIdentityLogout />}
                />
                <Route
                  path="identity-register"
                  element={<DocumentationIdentityRegister />}
                />
                <Route
                  path="identity-stats"
                  element={<DocumentationIdentityStats />}
                />
                <Route
                  path="identity-update"
                  element={<DocumentationIdentityUpdate />}
                />
                <Route
                  path="identity-update-self"
                  element={<DocumentationIdentityUpdateSelf />}
                />
                <Route
                  path="identity-view"
                  element={<DocumentationIdentityView />}
                />
                <Route
                  path="identity-view-self"
                  element={<DocumentationIdentityViewSelf />}
                />
                <Route
                  path="identity-groups"
                  element={<DocumentationIdentityGroups />}
                />
                <Route path="webhooks" element={<DocumentationWebhooks />} />
                <Route path="flows" element={<DocumentationFlows />} />
                <Route
                  path="flows-events"
                  element={<DocumentationFlowsEvents />}
                />
                <Route
                  path="flows-reference"
                  element={<DocumentationFlowsReference />}
                />
                <Route
                  path="identity-password-reset"
                  element={<DocumentationIdentityPasswordReset />}
                />
                <Route
                  path="identity-oauth"
                  element={<DocumentationIdentityOAuth />}
                />
                <Route
                  path="identity-oauth-google"
                  element={<DocumentationIdentityOAuthGoogle />}
                />
                <Route
                  path="identity-oauth-github"
                  element={<DocumentationIdentityOAuthGitHub />}
                />
                <Route
                  path="identity-oauth-apple"
                  element={<DocumentationIdentityOAuthApple />}
                />
                <Route
                  path="identity-oauth-microsoft"
                  element={<DocumentationIdentityOAuthMicrosoft />}
                />
                <Route
                  path="identity-oauth-discord"
                  element={<DocumentationIdentityOAuthDiscord />}
                />
                <Route
                  path="identity-oauth-facebook"
                  element={<DocumentationIdentityOAuthFacebook />}
                />
                <Route
                  path="identity-oauth-providers"
                  element={<DocumentationIdentityOAuthProviders />}
                />
                <Route
                  path="identity-oauth-start"
                  element={<DocumentationIdentityOAuthStart />}
                />
                <Route
                  path="identity-oauth-complete"
                  element={<DocumentationIdentityOAuthComplete />}
                />
                <Route
                  path="identity-self-providers"
                  element={<DocumentationIdentitySelfProviders />}
                />
                <Route
                  path="identity-self-provider-link"
                  element={<DocumentationIdentitySelfProviderLink />}
                />
                <Route
                  path="identity-self-provider-unlink"
                  element={<DocumentationIdentitySelfProviderUnlink />}
                />
                <Route
                  path="identity-password-reset-request"
                  element={<DocumentationIdentityPasswordResetRequest />}
                />
                <Route
                  path="identity-password-reset-confirm"
                  element={<DocumentationIdentityPasswordResetConfirm />}
                />
                <Route
                  path="identity-email-verification-request"
                  element={<DocumentationIdentityEmailVerificationRequest />}
                />
                <Route
                  path="identity-email-verification-confirm"
                  element={<DocumentationIdentityEmailVerificationConfirm />}
                />
                <Route
                  path="token-view-self"
                  element={<DocumentationTokenViewSelf />}
                />
                <Route path="flow-run" element={<DocumentationFlowRun />} />
                <Route
                  path="schema-sync"
                  element={<DocumentationSchemaSync />}
                />
                <Route
                  path="write-rules"
                  element={<DocumentationWriteRules />}
                />
                <Route
                  path="write-rules-reference"
                  element={<DocumentationWriteRulesReference />}
                />
                <Route
                  path="write-rules-recipes"
                  element={<DocumentationWriteRulesRecipes />}
                />
                <Route
                  path="list-rules-test"
                  element={<DocumentationListRulesTest />}
                />
                <Route
                  path="list-rule-denials"
                  element={<DocumentationListRuleDenials />}
                />
                <Route
                  path="command-line-tool"
                  element={<DocumentationCommandLineTool />}
                />
                <Route path="mcp" element={<DocumentationMcp />} />
                <Route
                  path="sync-schema"
                  element={<DocumentationSyncSchema />}
                />
                <Route
                  path="sync-schema-export"
                  element={<DocumentationSyncSchemaExport />}
                />
                <Route
                  path="sync-schema-move"
                  element={<DocumentationSyncSchemaMove />}
                />
                <Route path="msgpack" element={<DocumentationMessagePack />} />
                <Route path="json5" element={<DocumentationJSON5 />} />
              </Route>

              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BulletinProvider>
        </AuthProvider>
      </QueryParamProvider>
    </Router>
  );
};

export default App;
